'use client'

import { useState } from 'react'
import { useGetCreditHistoryQuery } from '@/api/user/organization/credits/get-credit-history-query'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { CreditPoolCardSkeleton, CreditPoolHistoryList } from '../../credit-pool-card'
import { CreditUsageTable } from '../../credit-usage-table'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Coins } from 'lucide-react'

const ITEMS_PER_PAGE = 10

export const CreditsTab = () => {
    const organizationId = useOrganizationId()
    const [poolPage, setPoolPage] = useState(1)

    const { data, isLoading } = useGetCreditHistoryQuery(organizationId, {
        page: poolPage,
        limit: ITEMS_PER_PAGE,
    })

    const creditPools = data?.data?.creditPools ?? []
    const summary = data?.data?.summary
    const meta = data?.meta

    const totalPages = meta ? Math.ceil(meta.totalCount / meta.limit) : 1

    const formatNumber = (num: number | undefined) => {
        if (num === undefined) return '—'
        return num.toLocaleString()
    }

    if (isLoading) {
        return (
            <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Skeleton className="h-[76px] rounded-xl" />
                    <Skeleton className="h-[76px] rounded-xl" />
                </div>
                <Skeleton className="h-9 w-[120px] rounded-md" />
                <div className="flex flex-col gap-4">
                    <Skeleton className="h-[36px] w-[240px] rounded-lg" />
                    <CreditPoolCardSkeleton />
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Summary Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#FAFAFA] rounded-xl p-4 border border-[#E5E7EB]">
                    <div className="text-[13px] text-[#6B7280] mb-1">Current Available</div>
                    <div className="text-[24px] font-semibold text-[#171717]">
                        {formatNumber(summary?.currentAvailable)}
                    </div>
                </div>
                <div className="bg-[#FAFAFA] rounded-xl p-4 border border-[#E5E7EB]">
                    <div className="text-[13px] text-[#6B7280] mb-1">Total Consumed</div>
                    <div className="text-[24px] font-semibold text-[#171717]">
                        {formatNumber(summary?.totalConsumedAllTime)}
                    </div>
                </div>
            </div>

            {/* Tabbed Content */}
            <Tabs defaultValue="usage">
                <TabsList>
                    <TabsTrigger value="usage">Credit Usage</TabsTrigger>
                    <TabsTrigger value="pools">Credits Received Overview</TabsTrigger>
                </TabsList>

                <TabsContent value="usage">
                    <CreditUsageTable />
                </TabsContent>

                <TabsContent value="pools">
                    <CreditPoolHistoryList
                        pools={creditPools}
                        isLoading={false}
                        emptyIcon={Coins}
                        emptyMessage="No credit history yet."
                        page={poolPage}
                        totalPages={totalPages}
                        onPageChange={setPoolPage}
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}
